import dateFormat from 'dateformat';
import MomentTimezone from 'moment-timezone';
import { Moment } from 'moment';
import { StacklineDate } from 'stackline-datetime';

const DAY_ID_FORMAT = 'YYYYMMDD';

export const moment = MomentTimezone;

/**
 *
 * @param {Date} date date to format as string
 * @param {string} mask check here https://github.com/felixge/node-dateformat#mask-options
 * @param {boolean} utc converts local time to UTC
 * @param {boolean} gmt converts timezone to GMT (keeping the time same i.e discard timezone)
 */
export function dateformat(date?: Date | string | number, mask?: string, utc?: boolean, gmt?: boolean) {
  return dateFormat(date, mask, utc, gmt);
}

function getFirstDateOfWeekISO8601(weekId: number | string): string {
  weekId = typeof weekId === 'string' ? parseInt(weekId, 10) : weekId;
  const year = weekId / 100;
  const weekIndex = (weekId % 100) - 1; // subtract 1 to have it start from 0
  //  1. get the first thursday of the year
  // Note: The ISO 8601 definition for week 01 is the week with the first Thursday
  // of the Gregorian year (i.e. of January) in it.
  const firstThursday = new Date(Date.UTC(year, 0));
  if (firstThursday.getUTCDay() !== 4) {
    firstThursday.setUTCMonth(0, 1 + ((4 - firstThursday.getUTCDay() + 7) % 7));
  }

  // try returning day id instead
  const date = MomentTimezone.utc(firstThursday)
    .tz('America/Los_Angeles') // all times come from the API in PST
    .add(7 * weekIndex, 'd')
    .add(-3, 'd');
  return date.format(DAY_ID_FORMAT);
}

export function getStartAndEndOfWeek(
  weekId: number | string | undefined,
  timeZoneLocalized: boolean = true
): { startDate: string | null; endDate: string | null } {
  if (!weekId) {
    return { startDate: null, endDate: null };
  }

  const timezone = timeZoneLocalized ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'utc';

  // Convert weekId to number if it's a string, ensuring proper handling of leading zeros
  const numericWeekId = +weekId;

  // Split the input into year and week
  const year = Math.floor(numericWeekId / 100);
  const week = numericWeekId % 100;

  // Validate week number doesn't exceed max weeks in year
  const maxWeeksInYear = StacklineDate.getSDSWeeksInYear(year);
  if (week < 1 || week > maxWeeksInYear) {
    return { startDate: null, endDate: null };
  }

  // Use StacklineDate to get start and end dates
  try {
    const startDateObj = StacklineDate.getStartDateSDS({ year, week, timezone });
    const endDateObj = StacklineDate.getEndDateSDS({ year, week, timezone });

    // StacklineDate operates in UTC, so format in UTC to avoid timezone shifts
    const startDate = moment.utc(startDateObj.toJSDate()).format(DAY_ID_FORMAT);
    const endDate = moment.utc(endDateObj.toJSDate()).format(DAY_ID_FORMAT);

    return { startDate, endDate };
  } catch (e) {
    console.error('Error in getStartAndEndOfWeek:', e);
    return { startDate: null, endDate: null };
  }
}

export const convertDayToString = (dateToConvert: Date): string => {
  const strFormat = 'YYYY-MM-DD';
  return moment(dateToConvert).format(strFormat);
};

/*
 * Gets the week id for date `d`
 */
export function getWeekId(date?: Date | string | number | any): number {
  let instance;
  if (typeof date === 'string' && date.length) {
    instance = new Date(date);
  } else if (typeof date === 'number') {
    instance = new Date(date);
  } else if (date instanceof Date) {
    instance = date;
  } else if (date && date._isAMomentObject) {
    instance = new Date(date.valueOf());
  } else {
    instance = new Date();
  }
  // Create a copy of this date object
  const target = new Date(instance.valueOf());

  // ISO week date weeks start on monday
  // so correct the day number
  // ** commented out as we have Sunday as Start of week day
  // const dayNr = (instance.getUTCDay() + 6) % 7; //
  const dayNr = instance.getUTCDay(); // Assumes Sunday as Start Of week

  // ISO 8601 states that week 1 is the week
  // with the first thursday of that year.
  // Set the target date to the thursday in the target week
  target.setUTCDate(target.getUTCDate() - dayNr + 3);

  // Store the millisecond value of the target date
  const firstThursday = target.valueOf();

  // Set the target to the first thursday of the year
  // First set the target to january first
  target.setUTCMonth(0, 1);
  // Not a thursday? Correct the date to the next thursday
  if (target.getUTCDay() !== 4) {
    target.setUTCMonth(0, 1 + ((4 - target.getUTCDay() + 7) % 7));
  }

  // The weeknumber is the number of weeks between the
  // first thursday of the year and the thursday in the target week
  const weekNumber = 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
  return target.getUTCFullYear() * 100 + weekNumber;
}

export function getPreviousWeekId(weekId = getWeekId(new Date()), weeksToSubtract = 1): number {
  const year = Math.floor(weekId / 100);
  const week = weekId % 100;

  try {
    // Get the start date of the given week using StacklineDate (SDS format)
    const startDateObj = StacklineDate.getStartDateSDS({ year, week, timezone: 'utc' });

    // Subtract the number of weeks (in milliseconds)
    const msToSubtract = weeksToSubtract * 7 * 24 * 60 * 60 * 1000;
    const resultTimestamp = startDateObj.valueOf() - msToSubtract;

    // Create a Date that represents the correct UTC day in local timezone
    // StacklineDate constructor uses local timezone methods, so we need to adjust
    const utcDate = new Date(resultTimestamp);
    const localDate = new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      12, 0, 0 // Use noon to avoid any edge cases
    );

    // Convert back to weekId using StacklineDate
    const result = new StacklineDate(localDate).getSDSWeekYear();
    return result.year * 100 + result.week;
  } catch (e) {
    console.error('Error in getPreviousWeekId:', e);
    // Fallback to old logic
    const startDate = getFirstDateOfWeekISO8601(weekId);
    return getWeekId(
      MomentTimezone.utc(startDate)
        .add(-7 * weeksToSubtract, 'd')
        .toDate()
    );
  }
}

export function getNextWeekId(weekId = getWeekId(new Date()), weeksToAdd = 1): number {
  const year = Math.floor(weekId / 100);
  const week = weekId % 100;

  if (weekId === 0 || !weekId) {
    console.warn('[getNextWeekId] Invalid weekId:', weekId);
    return 0;
  }

  try {
    // Get the start date of the given week using StacklineDate (SDS format)
    const startDateObj = StacklineDate.getStartDateSDS({ year, week, timezone: 'utc' });

    // Add the number of weeks (in milliseconds)
    const msToAdd = weeksToAdd * 7 * 24 * 60 * 60 * 1000;
    const resultTimestamp = startDateObj.valueOf() + msToAdd;

    // Create a Date that represents the correct UTC day in local timezone
    // StacklineDate constructor uses local timezone methods, so we need to adjust
    const utcDate = new Date(resultTimestamp);
    const localDate = new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      12, 0, 0 // Use noon to avoid any edge cases
    );

    // Convert back to weekId using StacklineDate
    const result = new StacklineDate(localDate).getSDSWeekYear();
    const nextWeekId = result.year * 100 + result.week;

    // Safety check
    if (nextWeekId <= weekId && weeksToAdd > 0) {
      console.error('[getNextWeekId] Result not greater than input! weekId:', weekId, 'nextWeekId:', nextWeekId, 'weeksToAdd:', weeksToAdd);
    }

    return nextWeekId;
  } catch (e) {
    console.error('[getNextWeekId] Error:', e, 'weekId:', weekId, 'year:', year, 'week:', week);
    // Fallback to old logic
    const startDate = getFirstDateOfWeekISO8601(weekId);
    return getWeekId(
      MomentTimezone.utc(startDate)
        .add(7 * weeksToAdd, 'd')
        .toDate()
    );
  }
}

export function isValidWeekId(weekId: number | string | undefined): boolean {
  const { startDate, endDate } = getStartAndEndOfWeek(weekId);
  return Boolean(startDate && endDate);
}

/**
 * Caps a weekId to the maximum valid week for that year.
 * Useful when subtracting years (e.g., prior-year calculations) where
 * the target year may have fewer weeks (52 vs 53).
 *
 * @param weekId - The weekId to validate and cap (format: YYYYWW)
 * @returns The capped weekId, ensuring the week number doesn't exceed the max for that year
 */
export function capWeekIdToValidWeek(weekId: number): number {
  const year = Math.floor(weekId / 100);
  const week = weekId % 100;

  const maxWeeksInYear = StacklineDate.getSDSWeeksInYear(year);

  if (week > maxWeeksInYear) {
    return year * 100 + maxWeeksInYear;
  }

  return weekId;
}

export function getNextYearWeekId(weekId: number): number {
  const year = Math.trunc(weekId / 100);
  const week = weekId % 100;
  const nextYear = year + 1;

  if (!weekId) {
    return null;
  }

  // Check if next year has enough weeks
  const maxWeeksInNextYear = StacklineDate.getSDSWeeksInYear(nextYear);

  // If week 53 doesn't exist in next year, go to week 1 of the year after
  if (week > maxWeeksInNextYear) {
    return (nextYear + 1) * 100 + 1;
  }

  return nextYear * 100 + week;
}

export function getNextYearDayId(dayId: number): number {
  const date = moment(dayId?.toString() || '', 'YYYYMMDD').add(1, 'year');
  return parseInt(date.format('YYYYMMDD'), 10);
}

export function getPreviousDayId(dayId: number): number {
  const date = moment(dayId?.toString() || '', 'YYYYMMDD').subtract(1, 'day');
  return parseInt(date.format('YYYYMMDD'), 10);
}

export function getYearForWeekId(weekId: number): number {
  const { startDate } = getStartAndEndOfWeek(weekId);
  return moment(startDate, 'YYYYMMDD').year();
  // return Math.trunc(weekId / 100);
}

/**
 * Only returns the year part of a week ID, regardless
 * of the year of the start date of the ISO week.
 * For example, 202401 -> 2024
 */
export function yearPartOfWeekId(weekId: number) {
  return Math.floor(weekId / 100);
}

export const lang = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const getLastDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 0).getDate();
};

export const yearMonthToMoment = (year: number, month: number) => {
  return moment(new Date(year, month - 1, getLastDayOfMonth(year, month)));
};

export const extractMonthYear = (date: Moment) => {
  return {
    year: date.year(),
    month: date.month() + 1
  };
};

/**
 * Converts formatted dates from the dispute table
 * @param date Takes in a string date formatted as M/D/YYYY
 */
export const invoiceDateToISOFormattedDate = (date: string) => {
  const dateObj = new Date(Date.parse(date));
  const isoDateStr = dateObj.toISOString();
  const formattedDate = `${isoDateStr.slice(0, -14)}`;
  return formattedDate;
};

/** Convert ISO formatted date string */
export const formatISODate = (date: string, format: string) => {
  const momentDate = moment(date);
  const formattedDate = momentDate.format(format);
  return formattedDate;
};

export const formatDateForDisputeTable = (stringISODate: string) => {
  if (!stringISODate || stringISODate === '') {
    return '';
  }

  const milliseconds = Date.parse(stringISODate);
  const date = new Date(milliseconds);
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

/**
 *
 * @param dateString Accepts a string formatted as YYYY-MM-DD such as 2023-01-10
 * @returns A formatted date string with a day suffix for display such as "Jan 10th, 2023"
 */
export const formatDateWithSuffix = (dateString) => {
  if (!dateString) {
    return '';
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const date = new Date(dateString.replace(/-/g, '/'));
  const day = date.getDate();
  const monthIndex = date.getMonth();
  const year = date.getFullYear();

  // Append the appropriate suffix to the day
  let daySuffix;
  if (day % 10 === 1 && day !== 11) {
    daySuffix = 'st';
  } else if (day % 10 === 2 && day !== 12) {
    daySuffix = 'nd';
  } else if (day % 10 === 3 && day !== 13) {
    daySuffix = 'rd';
  } else {
    daySuffix = 'th';
  }

  return `${months[monthIndex]} ${day}${daySuffix}, ${year}`;
};

export function dayIdToTimestamp(dayId: number) {
  const date = moment(dayId.toString(), 'YYYYMMDD');
  return date.valueOf();
}

export function timestampToDayId(timestamp: number) {
  const date = moment(timestamp);

  const dayId = parseInt(date.format('YYYYMMDD'), 10);
  return dayId;
}

/**
 * Converts a numeric date to a formatted date string in the format "MMM dd, yyyy"
 * @param numericDate - The numeric date to convert
 * @returns The formatted date string or an error message if the input is invalid
 */
export const formatNumericDateToMonthDayYear = (numericDate: number | string): string => {
  if (!numericDate) return '';

  // Convert to string if it's a number
  const dateStr = numericDate.toString();

  // Validate input format (should be 8 digits)
  if (dateStr.length !== 8 || isNaN(Number(dateStr))) {
    return 'Invalid date format';
  }

  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);

  const date = StacklineDate.fromObject({
    year: parseInt(year, 10),
    month: parseInt(month, 10),
    day: parseInt(day, 10)
  });

  return date.toFormat('MMM dd, yyyy');
};

/**
 * Converts a week ID to a Unix timestamp in milliseconds.
 * This will be the X value for trend charts
 */
export function weekIdToTimestamp(weekId: number) {
  // Split the input into year and week
  try {
    const year = Math.floor(weekId / 100);
    const week = weekId % 100;

    const date = StacklineDate.getEndDateSDS({ year, week, timezone: 'utc' });
    return date.valueOf();
  } catch (e) {
    console.error('Error converting week ID to timestamp:', e);
    return null;
  }
}

/**
 * Converts a Unix timestamp to a week ID.
 * Inverse of weekIdToTimestamp
 */
export function timestampToWeekId(timestamp: number) {
  try {
    const date = new Date(timestamp);
    // Use noon of the date to avoid timezone edge cases at midnight
    // This matches the approach used in getPreviousWeekId and getNextWeekId
    const noonDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
    const { week, year } = new StacklineDate(noonDate).getSDSWeekYear();
    return year * 100 + week;
  } catch (e) {
    console.error('Error converting timestamp to week ID:', e);
    return null;
  }
}

/**
 * Converts a Date to SDS weekId using StacklineDate.
 */
export function dateToSDSWeekId(date: Date): number {
  // Use noon of the date to avoid timezone edge cases at midnight
  const noonDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
  const { week, year } = new StacklineDate(noonDate).getSDSWeekYear();
  return year * 100 + week;
}

/**
 * Goes back N weeks from a given weekId using the SDS week system.
 * Properly handles year boundaries and week 53.
 *
 * @deprecated Use getPreviousWeekId instead - it now uses StacklineDate internally.
 *
 * @param weekId - The starting weekId (format: YYYYWW)
 * @param weeksToSubtract - Number of weeks to go back
 * @returns The weekId N weeks before the given weekId
 */
export function goBackNWeeksSDS(weekId: number, weeksToSubtract: number): number {
  return getPreviousWeekId(weekId, weeksToSubtract);
}

export function getLastWeekIdOfYear(year: number = moment().year()): number {
  const totalWeeks = StacklineDate.getSDSWeeksInYear(year);
  return year * 100 + totalWeeks;
}

export function getFirstWeekIdOfYear(year: number = moment().year()): number {
  return Number(`${year}01`);
}

export const formatDateStringWithSuffix = (dateString: string, formatStr?: string) => {
  const parsedDate = moment(dateString, 'YYYYMMDD');
  const month = parsedDate.format('MMM');
  const day = parsedDate.format('Do');

  if (formatStr) {
    return parsedDate.format(formatStr);
  }

  return `${month} ${day}, ${parsedDate.format('YYYY')}`;
};

interface FormatDateForAdjustmentDisplayParams {
  /**
   * A start week ID such as 202325 (week 25 of the year 2023)
   */
  startWeekId: string | number;
  /**
   * An end week ID such as 202326 (week 26 of the year 2023)
   */
  endWeekId: string | number;
  /**
   * If provided, will return the formatted date string using the provided format string.
   */
  momentFormatStr?: string;
}
/**
 * Accepts 2 week IDs, and returns the start date of the start week and the end date of the end week
 * as a formatted string: i.e Jun 4th, 2023 to Jun 10th, 2023.
 */
export const formatWeekIdsForAdjustmentDisplay = ({
  startWeekId,
  endWeekId,
  momentFormatStr
}: FormatDateForAdjustmentDisplayParams) => {
  const { startDate } = getStartAndEndOfWeek(startWeekId); // Returns a date string formatted as: YYYYMMDD
  const { endDate } = getStartAndEndOfWeek(endWeekId);

  const formattedStartDate = formatDateStringWithSuffix(startDate, momentFormatStr);
  const formattedEndDate = formatDateStringWithSuffix(endDate, momentFormatStr);

  return { formattedStartDate, formattedEndDate };
};

/**
 *
 * @param date A date object or an ISO-formatted date string such as 2023-08-23T14:30:00.000Z
 * @returns a string in YYYYMMDD format
 */
export const stripISODate = (date: Date | string) => {
  return moment(date).format('YYYYMMDD');
};

/**
 *
 * @param startWeekId
 * @param endWeekId
 * @returns the number of weeks between each start and end week
 */
export const calculateWeeksBetweenWeekIds = (startWeekId: string | number, endWeekId: string | number) => {
  // Parse week IDs into year and week components
  const startYear = Math.floor(Number(startWeekId) / 100);
  const startWeek = Number(startWeekId) % 100;
  const endYear = Math.floor(Number(endWeekId) / 100);
  const endWeek = Number(endWeekId) % 100;
  if (!startWeekId || !endWeekId) {
    return 0;
  }

  // Validate week numbers using StacklineDate
  const maxWeeksInStartYear = StacklineDate.getSDSWeeksInYear(startYear);

  try {
    // Use StacklineDate to get precise start and end dates
    const timezone = 'utc'; // Use UTC for consistent calculations
    const startDateObj = StacklineDate.getStartDateSDS({ year: startYear, week: startWeek, timezone });
    const endDateObj = StacklineDate.getEndDateSDS({ year: endYear, week: endWeek, timezone });

    // Calculate the difference in milliseconds and convert to weeks
    const diffInMs = endDateObj.valueOf() - startDateObj.valueOf();
    const diffInWeeks = Math.ceil(diffInMs / (7 * 24 * 60 * 60 * 1000)) + 1; // +1 to include both start and end weeks

    return Math.max(1, diffInWeeks);
  } catch (error) {
    console.error('Error calculating weeks between week IDs:', error);
    // Fallback to simple arithmetic calculation
    if (startYear === endYear) {
      return Math.max(1, endWeek - startWeek + 1);
    } else {
      const remainingWeeksInStartYear = maxWeeksInStartYear - startWeek + 1;
      const weeksInEndYear = endWeek;
      return remainingWeeksInStartYear + weeksInEndYear;
    }
  }
};

/**
 * Get an array of week IDs between a start and end week ID, inclusive
 */
export const getWeekIdRange = (startWeekId: number, endWeekId: number) => {
  if (startWeekId > endWeekId) {
    return [];
  }

  // Safety check for invalid inputs that could cause infinite loops
  if (!startWeekId || !endWeekId || startWeekId < 100000 || endWeekId < 100000) {
    console.error('[getWeekIdRange] Invalid week IDs detected:', { startWeekId, endWeekId });
    return [];
  }

  const weekIdRange: number[] = [];
  let curWeekId = startWeekId;
  let iterations = 0;
  const maxIterations = 200; // Safety limit - no valid range should exceed 200 weeks

  while (curWeekId <= endWeekId && iterations < maxIterations) {
    weekIdRange.push(curWeekId);
    const prevWeekId = curWeekId;
    curWeekId = getNextWeekId(curWeekId);
    iterations++;

    // Safety check: ensure we're actually progressing
    if (curWeekId <= prevWeekId) {
      console.error('[getWeekIdRange] getNextWeekId not progressing! prevWeekId:', prevWeekId, 'curWeekId:', curWeekId);
      break;
    }
  }

  if (iterations >= maxIterations) {
    console.error('[getWeekIdRange] Hit max iterations! Possible infinite loop. startWeekId:', startWeekId, 'endWeekId:', endWeekId);
  }

  return weekIdRange;
};

/**
 *  Get last Saturday, (this can be extended to different day, but couldn't think of other day)
 */
export const formattedLastSaturday = (formatString = 'MM/DD/YYYY') => moment().day(-1).format(formatString);

/**
 * Gets the date of Thanksgiving for a given year
 */
const findThanksGivingDate = (year = moment().year()) => {
  // Start at the 1st of November
  const start = moment().year(year).month('November').startOf('month');

  // Calculate offset from that day to the next occurrence of weekday required.
  // example 1: November 1st, 2023 is a Wednesday, so 11 - 3 = 8 days away from the next Thursday.
  // example 2: November 1st, 2024 is a Friday, so 11 - 5 = 6 days
  const offsetToNextThursday = 11 - start.day();

  // example 1: November 1st, 2023 is a Wednesday, so 8 % 7 = 1.
  // example 2: November 1st, 2024 is a Friday, so 6 % 7 = 6.
  const novemberFirstVersusThursdayMod = offsetToNextThursday % 7;

  // example 1 (2023): November 22nd is the earliest date Thanksgiving can be, 22 + 1 = 23 or the 23rd of November.
  // example 2 (2024): November 22nd is the earliest date Thanksgiving can be, 22 + 6 = 28 or the 28th of November.
  const thanksGivingDay = 22 + novemberFirstVersusThursdayMod;

  // Set the date to Thanksgiving
  const thanksgiving = start.clone().date(thanksGivingDay);

  return thanksgiving;
};

/**
 * Gets the week ID for Black Friday using SDS week format
 */
export const findBlackFridayWeekId = (year = moment().year()) => {
  // Get the Thanksgiving date
  const thanksgiving = findThanksGivingDate(year);
  // Black Friday is the day after Thanksgiving
  const blackFriday = thanksgiving.clone().add(1, 'days').toDate();

  // Get the week ID using StacklineDate (SDS format)
  const { week, year: weekYear } = new StacklineDate(blackFriday).getSDSWeekYear();
  return `${weekYear}${week.toString().padStart(2, '0')}`;
};

/**
 * Gets the week ID for Cyber Monday using SDS week format
 */
export const findCyberMondayWeekId = (year = moment().year()) => {
  // Get the Thanksgiving date
  const thanksgiving = findThanksGivingDate(year);
  // Cyber Monday is the Monday after Thanksgiving
  const cyberMonday = thanksgiving.clone().add(4, 'days').toDate();

  // Get the week ID using StacklineDate (SDS format)
  const { week, year: weekYear } = new StacklineDate(cyberMonday).getSDSWeekYear();
  return `${weekYear}${week.toString().padStart(2, '0')}`;
};

/**
 * Get total number of weeks in the given year using SDS week format.
 * Note:: Not all years have 53 weeks.
 */
export const getTotalWeekInTheYear = (year = moment().year()) => {
  return StacklineDate.getSDSWeeksInYear(year);
};

/**
 * Returns boolean based on the year contains 53 or 52.
 * This is a replacement of the `leap year logic` login we used to have.
 */
export const is53WeeksInTheYear = (year = moment().year()) => getTotalWeekInTheYear(year) === 53;

export function getPreviousYearWeekId(weekId: number): number {
  if (!weekId) {
    return 0;
  }
  const year = Math.floor(weekId / 100);
  const week = weekId % 100;
  const prevYear = year - 1;

  // Get the number of weeks in the previous year using SDS
  const weeksInPrevYear = StacklineDate.getSDSWeeksInYear(prevYear);

  // Cap the week to the max weeks in the previous year if needed
  const cappedWeek = Math.min(week, weeksInPrevYear);

  return prevYear * 100 + cappedWeek;
}

/**
 * Gets the YoY comparison week ID for a given week ID.
 * Uses the microservice API to get the proper comparison week,
 * with fallback to local calculation if API fails.
 *
 * @param weekId - The week ID to get comparison for (format: YYYYWW as number)
 * @returns The comparison week ID (e.g., 202502 for 202601)
 */
export async function getComparisonWeekId(weekId: number): Promise<number> {
  if (!weekId) {
    return 0;
  }

  const weekIdStr = weekId.toString().padStart(6, '0');

  try {
    // Use the StacklineDate library to fetch comparison week from API
    const comparisonWeekStr = await StacklineDate.getComparisonWeekFromId(weekIdStr);

    if (comparisonWeekStr) {
      return parseInt(comparisonWeekStr, 10);
    }

    // Fallback to local calculation if API returns no comparison week
    return getPreviousYearWeekId(weekId);
  } catch (error) {
    console.error('Error fetching comparison week from API, using fallback:', error);
    // Fallback to local calculation
    return getPreviousYearWeekId(weekId);
  }
}
